import { getB2bProductDetailSources, type ProductDetailField, type ProductDetailSetting } from '@api/b2b';
import { ActionIcon, Button, Select, Text, TextInput } from '@mantine/core';
import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { useQuery } from '@tanstack/react-query';
import React from 'react';

import styles from './b2b.module.css';
import { type ProductDetailDraft, savableDetails, units } from './utils';

const FIELD_LABEL: Record<ProductDetailField, string> = {
    category: 'Category',
    subCategory: 'Sub-category',
    productGroup: 'Product group',
    productType: 'Product type',
};
const MAX_DETAILS = 6;

const sourceKey = (source: ProductDetailDraft['source']): string | null =>
    !source ? null : 'field' in source ? `field:${source.field}` : `attr:${source.attributeId}`;
const sourceOf = (key: string): ProductDetailSetting['source'] =>
    key.startsWith('attr:') ? { attributeId: key.slice(5) } : { field: key.slice(6) as ProductDetailField };

/**
 * B2B settings → Product details: the columns (Size, Colour…) shown on the distributor catalogue,
 * in free-piece labels and on packing lists, each read from a product field or a custom attribute.
 * The picker shows what each field holds for this brand's products, so the right one is obvious.
 */
const ProductDetailsEditor: React.FC<{ value: ProductDetailDraft[]; onChange: (rows: ProductDetailDraft[]) => void }> = ({ value, onChange }) => {
    const { data: sources } = useQuery({ queryKey: ['b2b-product-detail-sources'], queryFn: getB2bProductDetailSources, staleTime: 5 * 60 * 1000 });
    const info = new Map<string, { filled: number; examples: string[] }>([
        ...(sources?.fields ?? []).map((f) => [`field:${f.field}`, f] as const),
        ...(sources?.attributes ?? []).map((a) => [`attr:${a.id}`, a] as const),
    ]);
    const groups = [
        { group: 'Product fields', items: (sources?.fields ?? []).map((f) => ({ value: `field:${f.field}`, label: FIELD_LABEL[f.field], disabled: f.filled === 0 })) },
        { group: 'Custom attributes', items: (sources?.attributes ?? []).map((a) => ({ value: `attr:${a.id}`, label: a.name, disabled: a.filled === 0 })) },
    ].filter((g) => g.items.length > 0);
    const update = (i: number, patch: Partial<ProductDetailDraft>) => onChange(value.map((r, j) => (j === i ? { ...r, ...patch } : r)));
    const ready = savableDetails(value);
    const sample = sources?.sample;

    return (
        <div className={styles.stack}>
            <Text size="xs" c="dimmed">
                Shown as columns on the distributor catalogue, in free-piece labels and on packing lists. Pick the field that holds each one; the list shows what
                each field holds for your products.
            </Text>
            {value.map((row, i) => (
                <div key={i} className={styles.row}>
                    <TextInput size="xs" label={i === 0 ? 'Column name' : undefined} placeholder="e.g. Size" value={row.label} onChange={(e) => update(i, { label: e.currentTarget.value })} w={180} />
                    <Select
                        size="xs"
                        label={i === 0 ? 'Take it from' : undefined}
                        placeholder="Choose a field"
                        data={groups}
                        value={sourceKey(row.source)}
                        onChange={(key) => update(i, { source: key ? sourceOf(key) : null })}
                        renderOption={({ option }) => {
                            const meta = info.get(option.value);
                            return (
                                <div className={styles.detailOption}>
                                    <div className={styles.between}>
                                        <span>{option.label}</span>
                                        <span className={styles.muted}>
                                            {units(meta?.filled ?? 0)} of {units(sources?.total ?? 0)}
                                        </span>
                                    </div>
                                    <span className={styles.muted}>{option.disabled ? 'Empty for your products' : `e.g. ${(meta?.examples ?? []).join(', ')}`}</span>
                                </div>
                            );
                        }}
                        comboboxProps={{ width: 380, position: 'bottom-start' }}
                        w={240}
                    />
                    <ActionIcon mt={i === 0 ? 20 : 0} variant="subtle" color="gray" aria-label="Remove this detail" onClick={() => onChange(value.filter((_, j) => j !== i))}>
                        <TrashIcon size={16} />
                    </ActionIcon>
                </div>
            ))}
            {value.length < MAX_DETAILS ? (
                <Button size="xs" variant={ButtonVariant.SUBTLE} leftSection={<PlusIcon size={14} />} w="fit-content" onClick={() => onChange([...value, { label: '', source: null }])}>
                    Add a detail
                </Button>
            ) : null}
            {sample && ready.length ? (
                <span className={styles.cardHint}>
                    Example: {sample.productId} →{' '}
                    {ready.map((d, i) => {
                        const shown = sample.values[sourceKey(d.source) as string];
                        return (
                            <React.Fragment key={d.label}>
                                {i ? ' · ' : ''}
                                {d.label}: {shown ? <strong>{shown}</strong> : <span className={styles.error}>empty</span>}
                            </React.Fragment>
                        );
                    })}
                </span>
            ) : null}
        </div>
    );
};

export default ProductDetailsEditor;
